import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../auth.service';

function stripQueryParams(url: string): string {
  return url.replace(/\?.*$/, '');
}

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);
  private readonly inj = inject(Injector);

  private readonly blockedListPages = ['/', '/landing', '/oauth/logout', '/oauth/redirect'];
  // TODO(WARP-695): get around the host part of the url
  private readonly blockedListCalls = ['/oauth2/token'];

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!Capacitor.isNativePlatform()) {
      return next.handle(req);
    }

    if (this.isInBlockedList(this.router.url, req.url)) {
      return next.handle(req);
    }

    return this.refreshOn401AfterCall(req, next);
  }

  private isInBlockedList(pageUrl: string, url: string): boolean {
    const strippedPgUrl = stripQueryParams(pageUrl);
    if (this.blockedListPages.includes(strippedPgUrl)) {
      // do not refresh token on these pages
      return true;
    }
    const strippedUrl = stripQueryParams(url);
    if (this.blockedListCalls.includes(strippedUrl)) {
      // do not refresh token for these calls
      return true;
    }
    return false;
  }

  private refreshOn401AfterCall(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse) {
          switch (err.status) {
            case HttpStatusCode.Unauthorized:
              return this.refreshTokenAndMakeCall(req, next);
            default:
              return throwError(() => err);
          }
        }

        return throwError(() => err);
      }),
    );
  }

  private refreshTokenAndMakeCall(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const authService = this.inj.get(AuthService); // This avoids cyclic dependencies
    return from(authService.refreshAccessToken()).pipe(
      switchMap(() =>
        next.handle(req.clone({ headers: req.headers.set('Authorization', `Bearer ${authService.sessionId()}`) })),
      ),
      catchError((err) => throwError(() => err)),
    );
  }
}
